import { ClientRateconRevision, RateconRevisionRouteStop } from '@numeo/types-main-web-app'
import { Badge } from '@/components/ds2'
import { RevisionNote } from './RateconRevisionDiff'

/** Booked vs revised stop lists, side by side, for a revision whose route changed shape.
 *
 *  A shape change means stop i in the booked route is NOT stop i in the revised con — a stop was
 *  added, dropped, reordered or replaced — so the backend deliberately reports no per-stop change
 *  and applies none. The dispatcher has to map the stops themselves, and to do that they need to see
 *  both routes whole rather than a field-by-field diff that would silently pair unrelated stops. */
interface Props {
    v1Route: RateconRevisionRouteStop[]
    v2Route: RateconRevisionRouteStop[]
    /** A narrow host (the Agent page's review drawer): fixed columns and wrapped stop names, so nothing is cut off. */
    compact?: boolean
}

const stopName = (stop: RateconRevisionRouteStop): string => {
    const place = [stop.city, stop.state].filter(Boolean).join(', ')
    // Facility first — it is what a dispatcher recognises a stop by. City/state is the fallback for
    // a con that names no facility, and the qualifier when it does.
    if (stop.facility && place) return `${stop.facility} · ${place}`
    return stop.facility || place || 'Unnamed stop'
}

const stopSchedule = (stop: RateconRevisionRouteStop): string => [stop.date, stop.time].filter(Boolean).join(' ') || '—'

/** `applied: false` marks a value that is shown for mapping only and will NOT be written to the
 *  load. Rendered muted rather than as the bold, committed-looking value the booked column uses —
 *  a dispatcher read the revised schedule here, took it for a detected change, accepted, and only
 *  the rate moved. The prose notice above said so; the value itself has to say so too. */
const StopCell = ({ stop, applied = true, wrap = false }: { stop?: RateconRevisionRouteStop; applied?: boolean; wrap?: boolean }) => {
    if (!stop) return <span className="text-xs text-foreground-muted">— no stop —</span>
    return (
        <div className={`flex ${wrap ? 'min-w-0 ' : ''}flex-col gap-1`}>
            <div className={`flex gap-1.5 ${wrap ? 'items-start' : 'items-center'}`}>
                <Badge variant="soft" type={stop.type === 'pickup' ? 'accent' : 'default'} size="sm" label={stop.type === 'pickup' ? 'PU' : 'DEL'} />
                <span className={`text-xs text-foreground ${wrap ? 'min-w-0 break-words' : 'truncate'}`}>{stopName(stop)}</span>
            </div>
            <span className={`truncate text-xs ${applied ? 'font-medium text-foreground' : 'text-foreground-muted'}`}>{stopSchedule(stop)}</span>
        </div>
    )
}

export const RateconRevisionRouteComparison = ({ v1Route, v2Route, compact = false }: Props) => {
    const rowCount = Math.max(v1Route.length, v2Route.length)

    return (
        <div className={`rounded-xl ${compact ? 'overflow-x-hidden' : 'overflow-x-auto'}`}>
            <table className={`w-full text-sm ${compact ? 'table-fixed' : ''}`}>
                {compact && (
                    <colgroup>
                        <col className="w-[12%]" />
                        <col className="w-[44%]" />
                        <col className="w-[44%]" />
                    </colgroup>
                )}
                <thead className="sticky top-0 z-[1] bg-surface">
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                        <th className="px-3 py-2">Stop</th>
                        <th className="px-3 py-2">Previous rate confirmation</th>
                        <th className="px-3 py-2">
                            <span className="flex items-center gap-1.5">
                                Revised route
                                <Badge variant="soft" type="warning" size="sm" label="NOT APPLIED" />
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {Array.from({ length: rowCount }, (_, index) => (
                        <tr key={index}>
                            <td className="px-3 py-2.5 align-top text-foreground-muted">{index + 1}</td>
                            <td className="px-3 py-2.5 align-top">
                                <StopCell stop={v1Route[index]} wrap={compact} />
                            </td>
                            <td className="px-3 py-2.5 align-top">
                                <StopCell stop={v2Route[index]} applied={false} wrap={compact} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

/** Explains why a revision with a changed route shows no per-stop diff and what the dispatcher has
 *  to do instead. Deliberately blunt about the consequence of accepting: the rate moves, the stops
 *  do not. */
export const RateconRevisionRouteShapeNotice = ({ revision }: { revision: Pick<ClientRateconRevision, 'v1' | 'v2' | 'changedFields'> }) => {
    const rateWillApply = revision.changedFields.includes('rate')

    return (
        <RevisionNote>
            Stops no longer line up ({revision.v1.route.length} booked, {revision.v2.route.length} revised), so no appointment is applied.
            {rateWillApply ? ' Accepting applies the rate only.' : ' Accepting changes nothing on the load.'}
        </RevisionNote>
    )
}
